import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Star, MessageCircle, Languages } from "lucide-react";
import { doctors, clinicInfo } from "../mock";

export default function Doctors() {
  return (
    <section id="doctors" className="relative px-4 md:px-8 py-20 md:py-28">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-14">
          <div>
            <span className="eyebrow">Meet our trusted medical team</span>
            <h2 className="section-title">Meet the doctors behind your care</h2>
          </div>
          <p className="text-[#4a4a63] leading-relaxed lg:max-w-md lg:ml-auto">
            Our doctors bring decades of dedicated training and community experience. All are certified by the Medical Board of Australia — you're in experienced, caring hands at every visit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,540px)_1fr] gap-10 lg:gap-14 items-start">
          {doctors.map((d) => (
            <React.Fragment key={d.id}>
              {/* Portrait card */}
              <div className="relative group">
                <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] bg-[#eae6f6] shadow-lg">
                  <img
                    src={d.image}
                    alt={d.name}
                    style={{ objectPosition: d.imagePosition || "center 20%" }}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                  <div className="absolute inset-x-4 bottom-4 bg-[#1a2464] rounded-2xl px-5 py-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-white font-bold text-lg leading-tight">{d.name}</div>
                      <div className="text-white/70 text-xs mt-1">{d.creds}</div>
                    </div>
                    <a
                      href={clinicInfo.hotdocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 w-11 h-11 rounded-full bg-[#3dc9b3] grid place-items-center hover:translate-x-1 transition-transform"
                      aria-label={`Book with ${d.name}`}
                    >
                      <ArrowRight className="w-5 h-5 text-white" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Bio card */}
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-[#3dc9b3]/15 text-[#1a2464] font-semibold text-xs px-4 py-1.5 rounded-full mb-4">
                  <Award className="w-3.5 h-3.5 text-[#3dc9b3]" />
                  {d.role}
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-[#1a2464] leading-tight">
                  {d.name}
                </h3>
                {d.tagline && (
                  <div className="mt-2 text-[#3dc9b3] font-semibold italic text-[15px] md:text-base">
                    “{d.tagline}”
                  </div>
                )}
                {d.nickname && (
                  <div className="text-[#4a4a63] text-sm mt-1 flex items-center gap-2">
                    <MessageCircle className="w-3.5 h-3.5 text-[#3dc9b3]" /> Known to patients as <span className="font-semibold text-[#1a2464]">“{d.nickname}”</span>
                  </div>
                )}
                {d.languages?.length > 1 && (
                  <div className="text-[#4a4a63] text-sm mt-1 flex items-center gap-2" data-testid={`${d.id}-languages`}>
                    <Languages className="w-3.5 h-3.5 text-[#3dc9b3]" /> Speaks <span className="font-semibold text-[#1a2464]">{d.languages.join(", ")}</span>
                  </div>
                )}
                <p className="text-[#4a4a63] leading-relaxed mt-6">{d.bio}</p>

                {d.interests?.length > 0 && (
                  <div className="mt-8">
                    <div className="text-[#1a2464] font-bold mb-3 text-sm uppercase tracking-widest">Special Interests</div>
                    <ul className="space-y-2">
                      {d.interests.map((it) => (
                        <li key={it} className="flex items-start gap-3">
                          <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#3dc9b3]/15 grid place-items-center">
                            <Star className="w-3.5 h-3.5 text-[#3dc9b3]" fill="#3dc9b3" />
                          </span>
                          <span className="text-[#1a1a2e] text-[15px]">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-8">
                  <a
                    href={clinicInfo.hotdocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 bg-[#1a2464] text-white font-semibold pl-6 pr-2 py-2 rounded-full hover:bg-[#141c52]"
                  >
                    Book with {d.name.replace("Dr. ", "Dr ")}
                    <span className="w-9 h-9 rounded-full bg-[#3dc9b3] grid place-items-center group-hover:translate-x-0.5 transition-transform">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </span>
                  </a>
                  <Link
                    to="/fees"
                    className="inline-flex items-center gap-2 border border-[#1a2464]/20 text-[#1a2464] font-semibold px-5 py-3 rounded-full hover:bg-[#1a2464] hover:text-white transition-colors"
                  >
                    Fees & Billing
                  </Link>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
